import { Box, Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { LuMails } from 'react-icons/lu';
import { RiListSettingsLine } from 'react-icons/ri';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { ShepherdTour } from 'react-shepherd';
import {
  ICountRange,
  IJob,
  IJobSearchFilterDTO,
  IUserDTO,
  amplitude,
  VmsType,
  getVmsTypesList,
  downloadJobs,
} from 'wisestep-api';
import 'shepherd.js/dist/css/shepherd.css';
import {
  WsButton,
  WsColors,
  WsDetailsCard,
  WsIconCurrency,
  WsIconPersons,
  WsIconPosition,
  WsIconProfile,
  WsIconTime,
  WsIconVisa,
  WsPagination,
  WsDetailsObj,
  formattedDateISO,
  WsListingLoader,
  WsFilterTags,
  IFilterList,
  WsDrawer,
  WsPotentialRevenue,
} from 'wisestep-base';
import JobCardFooter from 'wisestep-website/src/features/jobs/listing/components/jobCardFooter';
import JobCardRightChild from 'wisestep-website/src/features/jobs/listing/components/jobCardRightChild';
import JobListHeader from 'wisestep-website/src/features/jobs/listing/components/jobListHeader';
import JobListSideNav from 'wisestep-website/src/features/jobs/listing/components/jobListSideNav';

import { JobAdvanceFilterFormProps } from '../../../components/filters';
import IconTextLink from '../../../components/IconTextLink';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useUserContext, useWorkspaceContext } from '../../../context';
import { LayoutTheme } from '../../../features/candidates/utils/CandidateHelper';
import { downloadHelper } from '../../../features/dataDownload/helper/PayloadHelper';
import { JobBoardsAction, useJobAccessControl } from '../../../hooks/useJobAccessControl';
import ErrorLogger from '../../../providers/ErrorLogger';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { success, warning } from '../../../utils/NotificationMessages';
import { INotification, useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { paginationSettingsStore } from '../../../utils/PaginationSettingStore';
import { extractEmail } from '../../../utils/StringUtils';
import { tourOptions, jobListSteps } from '../../../utils/TourStepsAndOptions';
import useConsultantsStore from '../../importConsultants/listing/hooks/useConsultantsStore';
import {
  formatSalaryDetails,
  pushJobsFilters,
  setDefaultSearchFilterJobListing,
  transformClientRecruitersToEmailArray,
  transformIClientDTOsToStringArray,
} from '../helper';
import { getPotentialRevenueString } from '../utils/ListingHelper';

import JobsDownloadFields from './components/Download';
import PostedBy from './components/jobCardFooter/PostedBy';
import JobsBottomActionBar from './components/JobsBottomActionBar';
import { ListingSearchFilter } from './components/jobSearch';
import { ListingSearchProps } from './components/jobSearch/ListingSearchProps';
import { useJobStats } from './hooks';
import useJobsStore from './hooks/useJobsStore';
import useListingJobs, { IListingJobsState, PotentialRevenueDTO } from './hooks/useListingJobs';
import { defaultQuery, jobCrmStatesCount, JobSearchFilter } from './JObSearchFilterDefaultQuery';

const getJobDetails = (
  job: IJob,
  workAssignmentData: Map<string, any>,
  navigate: NavigateFunction,
  setNotification: (notification: INotification) => void,
  potentialRevenueVisible = false,
  potentialRevenuedata: PotentialRevenueDTO | undefined
): WsDetailsObj[] => {
  const taxTerms = job.taxTerms ? `On ${job.taxTerms}` : '';
  const array: WsDetailsObj[] = [];
  array.push({
    icon: <WsIconCurrency />,
    labelName: formatSalaryDetails(job, taxTerms),
    popOverText: 'Salary',
    cursor: 'pointer',
  });
  array.push({
    icon: <WsIconTime />,
    labelName: job.jobType ?? 'Not Available',
    popOverText: 'Job Type',
    cursor: 'pointer',
  });
  array.push({
    icon: <WsIconPosition color={WsColors.TertiaryShades700} />,
    labelName: `${job.numberOfPositions ?? 0} Positions`,
    popOverText: 'Number of Positions',
    cursor: 'pointer',
  });
  array.push({
    icon: (
      <Box pl={1.5}>
        <IconTextLink
          text={job.accountManagerName ?? 'Not Available'}
          icon={<WsIconProfile color={WsColors.TertiaryShades700} />}
          opacity={true}
          link={`/account-manager/details-page?jobId=${job.id}&goBackUrl=/jobs`}
        />
      </Box>
    ),
    labelName: '',
    popOverText: 'Name of Account Manager',
    cursor: 'pointer',
  });
  array.push({
    icon: (
      <Box pl={1.5}>
        <IconTextLink
          text={job.clientRecruiterName ? job.clientRecruiterName : 'Not Available'}
          link={
            job?.clientRecruiterName && !/.*xxxxxx.*/.test(job.clientRecruiterName)
              ? `/${job?.clientContactId}/client-contact-details?isChild=true&name=${
                  job?.companyName
                }&goBack=true&clientId=${job?.clientId ?? ''}&goBackUrl=/jobs`
              : ''
          }
          icon={<WsIconProfile color={WsColors.TertiaryShades700} />}
          opacity={true}
        />
      </Box>
    ),
    labelName: '',
    popOverText: 'Name of Client Recruiter',
    cursor: 'pointer',
  });
  array.push({
    icon: <WsIconVisa />,
    labelName:
      job.clientJobCode && job.clientJobCode?.length > 1 ? job.clientJobCode : 'Not Available',
    popOverText: 'Client Job Code',
    cursor: 'pointer',
  });
  potentialRevenueVisible &&
    array.push({
      icon: <WsPotentialRevenue />,
      labelName: getPotentialRevenueString({
        ...potentialRevenuedata,
        potentialRevenueCurrency: job.salaryCurrency || job.clientBillRateCurrency || '',
      } as PotentialRevenueDTO),
      popOverText: 'Potential Revenue Per position',
      cursor: 'pointer',
    });
  array.push({
    icon: (
      <Box pl={1.5}>
        <IconTextLink
          text={`${workAssignmentData?.['accepted'] ?? 0} of ${
            workAssignmentData?.['total'] ?? 0
          } recruiters accepted`}
          link={`/jobs-crm/${job.id}/true/2/pipeline`}
          icon={<WsIconPersons />}
          opacity={true}
        />
      </Box>
    ),
    labelName: '',
    popOverText: 'Accepted Recruiters ',
  });
  return array;
};

const Listing = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDownload, setOpenDownload] = useState(false);
  const [vmsTypeList, setVmsTypeList] = useState<VmsType[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const queryData = searchParams.get('d') ?? '';
  const [query, setQuery] = useState<JobSearchFilter>(
    queryData ? getDecodedQueryParams(queryData) : defaultQuery
  );
  const queryDTO = useMemo<IJobSearchFilterDTO>(() => {
    return {
      ...query,
      ...(query.clientList && { clientNames: transformIClientDTOsToStringArray(query.clientList) }),
      ...(query.clientRecruiterList && {
        clientRecruiterEmails: transformClientRecruitersToEmailArray(query.clientRecruiterList),
      }),
    };
  }, [query]);

  const [loader, setLoader] = useState<boolean>(false);
  const jobDetails: IListingJobsState = useListingJobs(queryDTO, setLoader);
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>(pushJobsFilters(query));
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const workspace = useWorkspaceContext();
  const navigate = useNavigate();
  const navigateToJobPosting = () => navigate('/job-boards');
  const navigateToEmailCampaigns = () => navigate('/jobs/email-campaigns');

  const { access: jobAccesControl } = useJobAccessControl([
    JobBoardsAction.DOWNLOAD_JOBS,
    JobBoardsAction.POTENTIAL_REVENUE_VISIBILITY,
  ]);

  const downloadJob = workspace.owns || jobAccesControl[0];

  const potentialRevenueVisible = workspace.owns || jobAccesControl[1];

  const navigateToPostJob = () => {
    amplitude('job_create_jobslist', {
      clickedOn: 'new job',
      pageName: 'jobs',
      pageUrl: window.location.href,
      workspaceName: workspace.name,
      planType: workspace.planType,
      interfaceType: 'Web',
    });
    navigate('/jobs/new');
  };
  const setBulkJobs = useJobsStore((state) => state.updateSelectedJobs);
  const unSelectJob = useJobsStore((state) => state.removeSelectedJob);
  const selectedJobs = useJobsStore((state) => state.jobs);
  const jobStats = useJobStats(queryDTO);
  const [pageNumber, setPageNumber] = useState<number>(1);
  // const [informationTemplateMappings, setInformationTemplateMappings] = useState();
  // const [evaluationTemplateMappings, setEvaluationTemplateMappings] = useState();
  const [defaultSearchFilterValues] = useState<ListingSearchProps>(
    setDefaultSearchFilterJobListing(query) ?? {}
  );
  const selectedIndex = (index: number, value: string) => {
    query.jobStatus = value;
    setQuery({ ...query });
  };
  const pageChange = (event, value) => {
    const updatedQuery = { ...query };
    updatedQuery.page = value;
    setQuery(updatedQuery);
    setPageNumber(value + 1);
  };
  const setConsultants = useConsultantsStore((state) => state.setSelectedConsultants);
  useEffect(() => {
    setBulkJobs(null);
    setConsultants([]);
  }, [setBulkJobs, setConsultants]);

  useEffect(() => {
    const getVmsTypes = async () => {
      const response = await getVmsTypesList();
      setVmsTypeList(response.data);
    };
    getVmsTypes();
  }, []);

  // useEffect(() => {
  //   const getInformationTemplateMappings = async () => {
  //     const jobsArray = jobDetails.jobListArr;
  //     if (jobsArray.length > 0) {
  //       const jobIds: (string | undefined)[] = jobsArray.map((job) => job.id as string);
  //       const response = await getInfoTemplateMappingsByJobIds(jobIds);
  //       setInformationTemplateMappings(response.data);
  //     }
  //   };
  //   const getEvaluationTemplateMappings = async () => {
  //     const jobsArray = jobDetails.jobListArr;
  //     if (jobsArray.length > 0) {
  //       const jobIds: (string | undefined)[] = jobsArray.map((job) => job.id as string);
  //       const response = await getEvaluationTemplateMappingsByJobIds(jobIds);
  //       setEvaluationTemplateMappings(response.data);
  //     }
  //   };
  //   getEvaluationTemplateMappings();
  //   getInformationTemplateMappings();
  // }, [jobDetails]);

  const pageRefresh = () => setQuery({ ...query });
  const search = (data: ListingSearchProps) => {
    amplitude('Job_Listing_Search', {
      clickedOn: 'Search',
      pageName: 'JobListing',
      pageUrl: window.location.href,
      workspaceName: workspace.name,
      planType: workspace.planType,
      interfaceType: 'Web',
    });
    const updatedQuery = { ...query };
    updatedQuery.page = 0;
    updatedQuery.query = data.query ?? '';
    updatedQuery.postedByAccountManagerEmails =
      data.accountMangers?.map((email) => extractEmail(email)) ?? [];
    data.fromDate?.setHours(0, 0, 0);
    data.toDate?.setHours(23, 59, 59);
    updatedQuery.postedFromDate = data.fromDate ? formattedDateISO(data.fromDate) : '';
    updatedQuery.postedToDate = data.toDate ? formattedDateISO(data.toDate) : '';
    if (data.minSubmissions && data.maxSubmissions) {
      const submissionsCountRange: ICountRange = {
        from: data.minSubmissions,
        to: data.maxSubmissions,
        includeLower: true,
        includeUpper: true,
      };
      updatedQuery.submissionsCountRange = submissionsCountRange;
    }
    setSearchParams({ d: getEncodedQueryParams(updatedQuery) });
    setPageNumber(1);
    setQuery(updatedQuery);
  };

  const updateQuery = (data: JobAdvanceFilterFormProps) => {
    const searchString = 'label';
    const updatedQuery = { ...query };
    updatedQuery.businessUnit = data.businessUnit?.split(searchString)[0].trim();
    updatedQuery.page = 0;
    updatedQuery.locations = data.locations ?? [];
    updatedQuery.jobType = data.jobType;
    updatedQuery.hiringFor = data.hiringFor;
    updatedQuery.jobStatus = isEmpty(data.jobStatus) ? 'All' : data.jobStatus;
    updatedQuery.postedOnJobBoards = data.jobBoards ?? '';
    updatedQuery.clientRecruiterList = data.clientRecruiterList ?? [];
    updatedQuery.postedByRecruiterEmails = data.recruiters ?? [];
    updatedQuery.clientList = data.clientList ?? [];
    updatedQuery.tags = data.tags ?? [];
    updatedQuery.jobSource = data.jobSource ?? [];
    updatedQuery.searchAcrossTeams = [];
    updatedQuery.postedOnJobBoardBy = data.postedOnJobBoardBy ?? [];
    updatedQuery.postedOnJobBoardName = data.postedOnJobBoardName ?? '';
    updatedQuery.workplacePreference = data?.workplacePreference ?? '';
    if (data.postedOnJobBoardFromDate && data.postedOnJobBoardFromDate != null) {
      updatedQuery.postedOnJobBoardFromDate = new Date(data.postedOnJobBoardFromDate);
      updatedQuery.postedOnJobBoardFromDate.setHours(0, 0, 0);
      updatedQuery.postedOnJobBoardFromDate = formattedDateISO(
        updatedQuery.postedOnJobBoardFromDate
      );
    } else {
      updatedQuery.postedOnJobBoardFromDate = undefined;
    }
    if (data.postedOnJobBoardToDate && data.postedOnJobBoardToDate != null) {
      updatedQuery.postedOnJobBoardToDate = new Date(data.postedOnJobBoardToDate);
      updatedQuery.postedOnJobBoardToDate.setHours(23, 59, 59);
      updatedQuery.postedOnJobBoardToDate = formattedDateISO(updatedQuery.postedOnJobBoardToDate);
    } else {
      updatedQuery.postedOnJobBoardToDate = undefined;
    }
    data.teams?.forEach((obj) => updatedQuery.searchAcrossTeams?.push(obj.id ?? ''));
    setSearchParams({ d: getEncodedQueryParams(updatedQuery) });
    return updatedQuery;
  };

  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: JobAdvanceFilterFormProps = { ...query };
      if (
        [
          'jobBoards',
          'jobType',
          'jobStatus',
          'postedOnJobBoardName',
          'workplacePreference',
          'businessUnit',
          'hiringFor',
        ].includes(idOfChip)
      )
        advancedFilterDefaults[idOfChip] = null;
      else if (idOfChip == 'postedOnJobBoardBetween') {
        advancedFilterDefaults.postedOnJobBoardFromDate = null;
        advancedFilterDefaults.postedOnJobBoardToDate = null;
      } else advancedFilterDefaults[idOfChip] = [];

      setQuery(updateQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      prev.splice(index, 1);
      return [...prev];
    });
  };

  const onApplyFilters = (data: JobAdvanceFilterFormProps) => {
    setQuery(updateQuery(data));
    setPageNumber(1);
    setAppliedFilters(pushJobsFilters(data));
  };

  const addSelectedJob = (job: IJob & { isSelected: boolean }) => {
    if (!job.isSelected) setBulkJobs(job);
    else unSelectJob(job);
    job.isSelected = !job.isSelected;
  };

  const getValue = (data: jobCrmStatesCount[], jobId) => data.find((job) => job.jobId == jobId);

  const onClearFilters = () => {
    setAppliedFilters([]);
    setQuery({ ...defaultQuery });
    setSearchParams({ d: getEncodedQueryParams(defaultQuery) });
    setDefaultSearchFilterJobListing(defaultQuery);
  };

  const handleDownloadAction = async (d: string[]) => {
    try {
      if (!downloadJob) {
        setNotification(warning('You do not have enough permissions to perform this action'));
        return;
      } else {
        downloadHelper(await downloadJobs(d, queryDTO));
        setNotification(success('Downloaded candidates'));
      }
    } catch (error) {
      console.log(error);
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };

  const onJobBoardClick = (jobBoardName: string) => {
    const today = new Date(new Date().toUTCString());
    const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
    fromDate.setHours(0, 0, 0);
    today.setHours(23, 59, 59);

    const data: JobAdvanceFilterFormProps = {};
    data.postedOnJobBoardName = jobBoardName;
    data.postedOnJobBoardBy = loggedInUser?.email ? [loggedInUser.email] : [];
    if (jobBoardName != 'Dice' && jobBoardName != 'TechFetch') {
      data.postedOnJobBoardFromDate = formattedDateISO(fromDate);
      data.postedOnJobBoardToDate = formattedDateISO(today);
    }
    onApplyFilters(data);
  };

  const onLocationClick = (job: IJob) => {
    const isAssigned =
      loggedInUser && job.assignedTo?.filter((user) => user.recruiterId == loggedInUser.id);

    (job.canManageJob || job.createdBy === loggedInUser.id || isEmpty(isAssigned)) &&
      navigate(`/jobs/${job.id}/edit`);
  };
  const isVms = (job) =>
    vmsTypeList.some(
      (vms) => job?.jobSource && vms.text.toLowerCase() === job?.jobSource.toLowerCase()
    );

  return (
    <LayoutTheme>
      <ShepherdTour steps={jobListSteps} tourOptions={tourOptions}>
        <JobListHeader />
        <Grid container my={2}>
          <Grid item xs={12} sx={{ ml: 8 }} container justifyContent="flex-end">
            <Stack direction="row" spacing={2} alignItems="right">
              {workspace.owns && (
                <WsButton
                  variant="outlined"
                  size="large"
                  startIcon={<RiListSettingsLine size={23} color={WsColors.TheBlues500} />}
                  onClick={navigateToJobPosting}
                >
                  Premium Job Postings
                </WsButton>
              )}
              <WsButton
                variant="outlined"
                size="large"
                startIcon={<LuMails fontSize={23} color={WsColors.TheBlues500} />}
                onClick={navigateToEmailCampaigns}
              >
                Email Campaigns
              </WsButton>

              <Box id="post_job_from_listing">
                <WsButton variant="contained" size="large" onClick={navigateToPostJob}>
                  Post New Job
                </WsButton>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Grid item xs={12} container>
          <Grid item xs={3}>
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              <JobListSideNav
                selectedIndexValue={selectedIndex}
                jobStats={jobStats}
                query={query}
                onJobBoardClick={onJobBoardClick}
              />
            </ErrorBoundary>
          </Grid>
          <Grid item xs={9}>
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              <Box>
                <ListingSearchFilter
                  onSearch={search}
                  onApplyFilters={onApplyFilters}
                  defaultSearchFilters={defaultSearchFilterValues}
                  defaultValues={query}
                  onClearFilters={onClearFilters}
                  handleDownloadAction={() => {
                    if (!downloadJob) {
                      setNotification(
                        warning('You do not have enough permissions to perform this action')
                      );
                      return;
                    } else {
                      setOpenDownload(true);
                    }
                  }}
                />
                <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
              </Box>
            </ErrorBoundary>
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              <Grid item xs={12} container justifyContent="flex-end" mb={2}>
                <WsPagination
                  count={Math.ceil(
                    jobDetails.totalJobs / Number(paginationSettings.jobsListPagination)
                  )}
                  onChange={pageChange}
                  itemsPerPage={Number(paginationSettings.jobsListPagination)}
                  pageNumber={query.page}
                  defaultPage={pageNumber}
                  isPageCount={true}
                  type="Jobs"
                  totalCount={jobDetails.totalJobs}
                />
              </Grid>
            </ErrorBoundary>
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              {jobDetails.jobListArr.length > 0 && !loader ? (
                <Box>
                  {jobDetails.jobListArr?.map((job, index) => (
                    <Box my={2} key={index}>
                      <WsDetailsCard
                        onTitleClick={() => openUrlInNewTab(`/jobs-crm/${job.id}/false/0/pipeline`)}
                        title={job.title ?? ''}
                        titleCode={job.jobCode ?? ''}
                        clientName={`@${job.companyName ?? ''}`}
                        job={job}
                        locationsArray={job.locations ?? []}
                        vmsFlag={isVms(job)}
                        visibilityFlag={job.createdOn == job.modifiedOn}
                        onLocationClick={() => onLocationClick(job)}
                        remoteJob={job.remoteJob}
                        detailsArray={getJobDetails(
                          job,
                          jobDetails.workAssignmentData?.[job.id ?? ''],
                          navigate,
                          setNotification,
                          potentialRevenueVisible,
                          jobDetails.potentialRevenueData?.find((data) => data.id === job.id)
                        )}
                        rightChildren={
                          <JobCardRightChild
                            pageRefresh={pageRefresh}
                            job={job}
                            loggedInUserId={loggedInUser?.email ?? ''}
                            canManageJob={job.canManageJob ?? false}
                          />
                        }
                        footer={
                          // <JobCardFooter
                          //   job={job}
                          //   stats={candidateStats.find((obj) => obj.jobId === job.id)}
                          // />
                          <JobCardFooter
                            job={job}
                            stats={{
                              jobId: job.id ?? '',
                              mapped:
                                getValue(jobDetails.candidateStatsES, job.id)?.mappingsByJob ?? 0,
                              applied:
                                getValue(jobDetails.candidateStatsES, job.id)?.applicationsByJob ??
                                0,
                              submitted:
                                getValue(jobDetails.candidateStatsES, job.id)?.submissionsByJob ??
                                0,
                            }}
                          />
                        }
                        gridXs={3}
                        isActiveCard={!job.isClosedForAssigningRecruiters}
                        skillsArray={job.skills}
                        jobSourceArray={job.jobSource ? [job.jobSource] : []}
                        tagsArray={job.tags}
                        postedByChildren={
                          <PostedBy
                            createdOn={String(job.createdOn)}
                            teamName={job.team?.name ?? ''}
                            teamId={job.team?.id ?? ''}
                            loggedInUserId={loggedInUser?.email ?? ''}
                            assignedTo={job.assignedTo ?? []}
                            canManageJob={job.canManageJob ?? false}
                            jobOwner={job.jobOwner ?? ''}
                            createdBy={job.createdBy ?? ''}
                            modifiedOn={job.modifiedOn ? job.modifiedOn : new Date()}
                            modifiedBy={job.modifiedBy ?? ''}
                          />
                        }
                        isCheckBox={true}
                        checkBoxOnclick={() => addSelectedJob(job)}
                        isSelected={job.isSelected ?? false}
                        disableCheckBox={job?.isClosedForAssigningRecruiters || !job.canManageJob}
                        informationTemplates={
                          jobDetails?.informationTemplateMappings?.[job.id as string] ?? []
                        }
                        evaluationTemplates={
                          jobDetails?.evaluationTemplateMappings?.[job.id as string] ?? []
                        }
                      />
                    </Box>
                  ))}

                  <Box>
                    <Grid item xs={12} container justifyContent="flex-end" mt={2}>
                      {jobDetails.totalJobs > 0 && (
                        <WsPagination
                          count={Math.ceil(
                            jobDetails.totalJobs / Number(paginationSettings.jobsListPagination)
                          )}
                          onChange={pageChange}
                          itemsPerPage={Number(paginationSettings.jobsListPagination)}
                          pageNumber={query.page}
                          defaultPage={pageNumber}
                          isPageCount={false}
                          type="Jobs"
                          totalCount={jobDetails.totalJobs}
                        />
                      )}
                    </Grid>
                  </Box>
                </Box>
              ) : loader ? (
                <WsListingLoader />
              ) : (
                <NoDataFoundCard text="No Jobs Found" />
              )}
            </ErrorBoundary>
          </Grid>
        </Grid>
        {selectedJobs.length > 0 && <JobsBottomActionBar jobListArr={jobDetails.jobListArr} />}
      </ShepherdTour>
      <WsDrawer
        title="Download Fields"
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        size="medium"
        headerSize="medium"
      >
        <JobsDownloadFields
          selectedFields={(d) => handleDownloadAction(d)}
          close={() => setOpenDownload(false)}
        />
      </WsDrawer>
    </LayoutTheme>
  );
};

export default Listing;
